// Generic HTTP leaderboard adapter for a custom backend (non-Supabase).
// Talks to { baseUrl, token } over REST.
//
//   POST {baseUrl}/leaderboards/{boardId}/scores   body { name, score, meta }
//   GET  {baseUrl}/leaderboards/{boardId}/top?limit=N
//        -> [{ rank, name, score, meta, isMe }] | { entries: [...] }

export class RemoteLeaderboard {
  constructor({ baseUrl, token } = {}) {
    if (!baseUrl) throw new Error('[RemoteLeaderboard] leaderboard.remote.baseUrl is required.');
    this.baseUrl = baseUrl.replace(/\/+$/, '');
    this.token = token || null;
  }

  _headers(extra = {}) {
    const h = { ...extra };
    if (this.token) h.Authorization = `Bearer ${this.token}`;
    return h;
  }

  async submit(boardId, { name, score, meta } = {}) {
    const res = await fetch(`${this.baseUrl}/leaderboards/${encodeURIComponent(boardId)}/scores`, {
      method: 'POST',
      headers: this._headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ name, score: Number(score) || 0, meta: meta ?? null }),
    });
    if (!res.ok) throw new Error(`[RemoteLeaderboard] submit failed: ${res.status}`);
    return res.json().catch(() => ({ ok: true }));
  }

  async top(boardId, limit = 10) {
    const url = `${this.baseUrl}/leaderboards/${encodeURIComponent(boardId)}/top?limit=${limit}`;
    const res = await fetch(url, { headers: this._headers() });
    if (!res.ok) throw new Error(`[RemoteLeaderboard] top failed: ${res.status}`);
    const data = await res.json();
    const rows = Array.isArray(data) ? data : (data.entries || []);
    return rows.map((r, i) => ({
      rank: Number(r.rank ?? i + 1),
      name: r.name ?? r.display_name,
      score: Number(r.score) || 0,
      meta: r.meta ?? null,
      isMe: !!r.isMe,
    }));
  }
}
